import { graphql, navigate } from 'gatsby';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { blindPostRequest } from '../../utils/network';
import PostHogHelper from '../../utils/posthog-helper';
import { getPropValues } from '../../utils/helpers';
import Consent from '../fragments/consent';
import FormError from '../fragments/formError';
import Honey, { honeyGenerator } from '../fragments/honey';
import Submit from '../fragments/submit';
import AbuseForm from '../forms/abuse-revamp';
import { CapCaptcha } from '../reusable/CapCaptcha';

export interface MultiformProps {
  block: {
    css_class_name: string;
    side_image: string;
    side_image_alt: string;
    subscribe_url: string;
    thank_you_url: string;
    consent_text: string;
    settings: MultiformSettings;
    columns: MultiformColumn[];
  };
  includeAbuseInUrl?: boolean;
}

type MultiformSettings = {
  margin_bottom: string;
  css_class_name: string;
  custom_css: string;
};

type MultiformColumn = {
  title: string;
  text: string;
  image: string;
  issue_types: string[];
};

const typeMap: Record<string, number> = {
  support: 0,
  abuse: 1,
  billing: 2,
};

const Multiform = ({ block }: MultiformProps) => {
  const requestUrl = getPropValues(
    block.subscribe_url,
    process.env.GATSBY_API_ENDPOINT + '/contact/submit-form'
  );
  const thankYouPageUrl = getPropValues(
    block.thank_you_url,
    'https://bunny.net/thank-you'
  );
  const consentText = getPropValues(
    block.consent_text,
    'I have read and agree to the bunny.net <a href="/privacy">Privacy Policy</a>.'
  );
  const consentRef = useRef<HTMLInputElement>(null);
  const validRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const getInitialSelected = () => {
    if (typeof window === 'undefined') return 0;
    const searchParams = new URLSearchParams(window.location.search);
    const type = searchParams.get('type')?.toLowerCase() || '';
    const index = typeMap[type];
    return typeof index === 'number' ? index : 0; // fallback to support if missing
  };

  const [selected, setSelected] = useState(getInitialSelected);
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ready, setReady] = useState(false);
  const [captchaIssue, setCaptchaIssue] = useState(false);
  const [values, setValues] = useState({
    FirstName: '',
    LastName: '',
    ContactEmail: '',
    IssueType: block.columns[selected]?.issue_types[0],
    Message: '',
    PowToken: '',
  });

  const honeyHash = honeyGenerator();

  const honeyPots = {
    FirstName: '',
    LastName: '',
    ContactEmail: '',
    IssueType: '',
    Message: '',
  };
  (Object.keys(honeyPots) as Array<keyof typeof honeyPots>).forEach(
    key => (honeyPots[key] = key + honeyHash)
  );

  const changeHandler: any = (event: any) => {
    const stateKey = event.target.name.split(honeyHash)[0];

    setValues({
      ...values,
      [stateKey]: event.target.value,
    });

    /*
     * In React land, minLength doesn't work for HTML Textarea.
     * We have to check min length manually.
     */
    if (event.target instanceof HTMLTextAreaElement) {
      checkTextareaLength();
    }
  };

  const isPoppingState = useRef(false);

  // 1. On mount: read 'type' from URL and set selected
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const type = searchParams.get('type')?.toLowerCase() || '';
    const matchedIndex = typeMap[type];
    if (typeof matchedIndex === 'number') {
      setSelected(matchedIndex);
    }
    setReady(true); // ✅ Now ready to render
  }, []);

  // 2. Listen to back/forward navigation and update selected
  useEffect(() => {
    const handlePopState = () => {
      isPoppingState.current = true;

      const searchParams = new URLSearchParams(window.location.search);
      const type = searchParams.get('type')?.toLowerCase() || '';

      const matchedIndex = typeMap[type];
      if (typeof matchedIndex === 'number') {
        setSelected(matchedIndex);
      }

      // Clear the flag in the next tick so the next [selected] effect doesn't fire pushState
      requestAnimationFrame(() => {
        isPoppingState.current = false;
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 3. Update the URL when selected changes — but NOT if we're in a popstate event
  useEffect(() => {
    if (isPoppingState.current) return;

    const selectedType = Object.entries(typeMap).find(
      ([, index]) => index === selected
    )?.[0];

    if (!selectedType) return;

    const params = new URLSearchParams(window.location.search);
    params.set('type', selectedType);

    if (selectedType !== 'abuse') {
      params.delete('report');
    }

    const newUrl =
      window.location.pathname + '?' + params.toString() + window.location.hash;

    if (newUrl !== window.location.href) {
      window.history.pushState({}, '', newUrl);
    }
  }, [selected]);

  const ERROR_MESSAGE = `Please lengthen the message to at least 10 characters.`;
  const checkTextareaLength = (MIN_TEXTAREA_LENGTH = 10) => {
    if (values.Message.length < MIN_TEXTAREA_LENGTH) {
      messageRef.current?.setCustomValidity(ERROR_MESSAGE);
      return false;
    } else {
      messageRef.current?.setCustomValidity('');
      return true;
    }
  };

  const handleSubmit: React.FormEventHandler = async event => {
    event.preventDefault();

    if (!values.PowToken) {
      setCaptchaIssue(true);
      return;
    } else setCaptchaIssue(false);

    const postedSuccessfully = await blindPostRequest(requestUrl, {
      // Property names are defined by Rest API
      FullName: `${values.FirstName} ${values.LastName}`,
      ContactEmail: values.ContactEmail,
      Message: `Reason: ${values.IssueType} - Message: ${values.Message}`,
      IsValid: `${validRef.current?.checked}`,
      Title: `${block.columns[selected]?.title} - ${values.IssueType}`,
      PowToken: values.PowToken,
    });

    await PostHogHelper.promiseToCaptureEmail(values.ContactEmail);

    setSubmitted(true);

    if (postedSuccessfully) {
      setSuccess(true);
      navigate(thankYouPageUrl);
    } else {
      setSuccess(false);
    }
  };

  if (!ready) return null;

  return (
    <section className={`block__multi-form block ${block.css_class_name}`}>
      <div className='container'>
        <div className='row'>
          <ul className='multiform__columns'>
            {block.columns.map((column, index) => (
              <li
                className={`multiform__column ${
                  selected === index ? 'multiform__column--selected' : ''
                }`}
                key={index}
                onClick={() => setSelected(index)}
              >
                <div className='multiform-column__icon'>
                  <img
                    src={column.image}
                    alt='Bunny Logo'
                  />
                </div>
                <div className='multiform-column__text'>
                  <h4>{column.title}</h4>
                  <p>{column.text}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className='multiform__wrapper'>
            {selected === 1 && <AbuseForm block={block} />}
            {selected !== 1 && (
              <form
                className='multiform__form'
                onSubmit={handleSubmit}
              >
                <img
                  src={block.side_image}
                  alt={block.side_image_alt}
                  title={block.side_image_alt}
                  className='multiform__image'
                />
                <div className='multiform-item'>
                  <label
                    htmlFor={honeyPots.FirstName}
                    className='multiform-item__pre'
                  >
                    First Name
                  </label>
                  <input
                    id={honeyPots.FirstName}
                    name={honeyPots.FirstName}
                    type='text'
                    className='multiform-item__info'
                    placeholder='Your first name'
                    value={values.FirstName}
                    onChange={changeHandler}
                    required
                  />
                </div>
                <div className='multiform-item'>
                  <label
                    htmlFor={honeyPots.LastName}
                    className='multiform-item__pre'
                  >
                    Last Name
                  </label>
                  <input
                    id={honeyPots.LastName}
                    name={honeyPots.LastName}
                    type='text'
                    className='multiform-item__info'
                    placeholder='Your last name'
                    value={values.LastName}
                    onChange={changeHandler}
                    required
                  />
                </div>
                <div className='multiform-item'>
                  <label
                    htmlFor={honeyPots.ContactEmail}
                    className='multiform-item__pre'
                  >
                    Email
                  </label>
                  <input
                    id={honeyPots.ContactEmail}
                    name={honeyPots.ContactEmail}
                    type='email'
                    className='multiform-item__info'
                    placeholder='you@company.com'
                    value={values.ContactEmail}
                    onChange={changeHandler}
                    required
                  />
                </div>
                <div className='multiform-item'>
                  <label
                    htmlFor={honeyPots.IssueType}
                    className='multiform-item__pre'
                  >
                    How can we help you?
                  </label>
                  <select
                    id={honeyPots.IssueType}
                    name={honeyPots.IssueType}
                    className='multiform-item__issues'
                    value={values.IssueType}
                    onChange={changeHandler}
                  >
                    {block.columns[selected]?.issue_types.map(
                      (IssueType, index) => (
                        <option
                          key={index}
                          value={IssueType}
                        >
                          {IssueType}
                        </option>
                      )
                    )}
                  </select>
                  <div className='multiform-item__toggle' />
                </div>
                <div className='multiform-item multiform-item--span'>
                  <label
                    htmlFor={honeyPots.Message}
                    className='multiform-item__pre'
                  >
                    Message
                  </label>
                  <textarea
                    id={honeyPots.Message}
                    name={honeyPots.Message}
                    className='multiform-item__message'
                    placeholder='Tell us about your project and how bunny.net can help you.'
                    cols={30}
                    rows={10}
                    value={values.Message}
                    onChange={changeHandler}
                    ref={messageRef}
                    required
                  ></textarea>
                </div>
                <Consent
                  consentRef={consentRef}
                  formStyle='multiform-item--span'
                  text={consentText}
                />
                <CapCaptcha
                  className='multiform-item--span mx-auto'
                  onSolve={token => {
                    setValues(prev => ({ ...prev, PowToken: token }));
                  }}
                />
                {captchaIssue && (
                  <div className='error-message multiform-item--span mx-auto !mb-0'>
                    Please verify that you are human.
                  </div>
                )}
                <Submit wrapperStyle='multiform-item--span'>Let's Talk</Submit>

                <Honey validRef={validRef} />
                <FormError
                  submitted={submitted}
                  success={success}
                  className='multiform-item--span text-centered'
                />
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Multiform;

export const multiformQuery = graphql`
  fragment MultiformFragment on Block {
    side_image
    side_image_alt
    consent_text
    columns {
      title
      text
      image
      issue_types
    }
  }
`;
